import plainTextFromDangerouslySetInnerHtml from './plainTextFromDangerouslySetInnerHtml';
import { getLocaleDateString } from '@cvent/nucleus-core-datetime-utils';
import * as validators from './validators';

export const BlankChoice = Symbol('BlankChoice');
export const NAChoice = Symbol('NAChoice');
export const OtherChoice = Symbol('OtherChoice');

export const choiceRequired = (
  translate,
  errorMessage = 'NucleusWidgets_Validations_RequiredField__resx'
) => ({
  validator(options) {
    if (options.hasOwnProperty('selectedValue')) {
      return options.selectedValue && options.selectedValue !== BlankChoice;
    }
    return options.selectedValues && options.selectedValues.length > 0;
  },
  errorMessage(value, field) {
    return translate(errorMessage, {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const choicesNumberSelected = (translate, min, max) => ({
  validator(options) {
    if (options.selectedValues.includes(NAChoice)) {
      return true;
    }
    return (
      options.selectedValues &&
      (options.selectedValues.length === 0 ||
        ((typeof min !== 'number' || options.selectedValues.length >= min) &&
          (typeof max !== 'number' || options.selectedValues.length <= max)))
    );
  },
  errorMessage() {
    let message;
    if (typeof min === 'number' && typeof max === 'number') {
      message =
        min === max
          ? 'NucleusWidgets_Validations_NumberOfChoicesSelectedExactly__resx'
          : 'NucleusWidgets_Validations_NumberOfChoicesSelectedBetween__resx';
    } else if (typeof min === 'number') {
      message = 'NucleusWidgets_Validations_NumberOfChoicesAtLeast__resx';
    } else if (typeof max === 'number') {
      message = 'NucleusWidgets_Validations_NumberOfChoicesAtMost__resx';
    }
    return translate(message, {
      min: typeof min === 'number' ? min.toString() : min,
      max: typeof max === 'number' ? max.toString() : max
    });
  }
});

export const textRequired = (
  translate,
  errorMessage = 'NucleusWidgets_Validations_RequiredField__resx'
) => ({
  validator: validators.requiredValidator,
  errorMessage(value, field) {
    return translate(errorMessage, {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const textMinLength = (translate, minLength) => ({
  validator: validators.optionalValidator(
    validators.minLengthValidator(minLength)
  ),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_MinLengthField__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field),
      length: minLength
    });
  }
});

export const textMaxLength = (translate, maxLength) => ({
  validator: validators.optionalValidator(
    validators.maxLengthValidator(maxLength)
  ),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_MaxLengthField__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field),
      length: maxLength
    });
  }
});

export const textNumberInRange = (translate, min, max) => ({
  validator: validators.optionalValidator(validators.numberValidator(min, max)),
  errorMessage(value, field) {
    let message;
    if (typeof min === 'number' && typeof max === 'number') {
      message = 'NucleusWidgets_Validations_NumberFormatFieldBetween__resx';
    } else if (typeof min === 'number') {
      message = 'NucleusWidgets_Validations_NumberFormatFieldGreaterThan__resx';
    } else if (typeof max === 'number') {
      message = 'NucleusWidgets_Validations_NumberFormatFieldLessThan__resx';
    } else {
      message = 'NucleusWidgets_Validations_NumberFormatFieldAny__resx';
    }
    return translate(message, {
      field: plainTextFromDangerouslySetInnerHtml(field),
      min: typeof min === 'number' ? min.toString() : min,
      max: typeof max === 'number' ? max.toString() : max
    });
  }
});

export const textCurrency = translate => ({
  validator: validators.optionalValidator(validators.currencyValidator),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_CurrencyFormat__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const textDecimal = translate => ({
  validator: validators.optionalValidator(validators.decimalValidator),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_DecimalFormat__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const textUSPhoneNumber = translate => ({
  validator: validators.optionalValidator(validators.usPhoneNumberValidator),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_USPhoneNumberFormat__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const textEmailAddress = translate => ({
  validator: validators.optionalValidator(validators.emailFormatValidator),
  errorMessage(value, field) {
    return translate('NucleusWidgets_Validations_EmailFormatField__resx', {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const textSocialSecurityNumber = translate => ({
  validator: validators.optionalValidator(
    validators.socialSecurityNumberValidator
  ),
  errorMessage(value, field) {
    return translate(
      'NucleusWidgets_Validations_SocialSecurityNumberFormat__resx',
      { field: plainTextFromDangerouslySetInnerHtml(field) }
    );
  }
});

export const dateRequired = (
  translate,
  errorMessage = 'NucleusWidgets_Validations_RequiredField__resx'
) => ({
  validator: validators.dateRequiredValidator,
  errorMessage(value, field) {
    return translate(errorMessage, {
      field: plainTextFromDangerouslySetInnerHtml(field)
    });
  }
});

export const dateFormat = (translate, locale, formatOptions) => ({
  validator: validators.dateFormatValidator,
  errorMessage() {
    return translate('NucleusWidgets_Validations_DateFormat__resx', {
      format: getLocaleDateString(locale, formatOptions)
    });
  }
});

export const dateRange = (translate, minDate, maxDate) => ({
  validator: validators.dateRangeValidator,
  errorMessage() {
    if (minDate && maxDate) {
      return translate('NucleusWidgets_Validations_DateBetween__resx', {
        startDate: new Date(minDate),
        endDate: new Date(maxDate)
      });
    } else if (maxDate) {
      return translate('NucleusWidgets_Validations_DateBefore__resx', {
        endDate: new Date(maxDate)
      });
    } else if (minDate) {
      return translate('NucleusWidgets_Validations_DateAfter__resx', {
        startDate: new Date(minDate)
      });
    }
  }
});

export const dateTimeGroupValidations = (
  translate,
  required,
  useSeconds,
  locale,
  formatOptions,
  minDate,
  maxDate,
  requiredErrorMessage = 'NucleusWidgets_Validations_RequiredField__resx'
) => ({
  validator(dateInput) {
    return (
      (!required || validators.dateRequiredValidator(dateInput)) &&
      validators.dateFormatValidator(dateInput) &&
      validators.timeFormatValidator(dateInput, useSeconds) &&
      validators.dateRangeValidator(dateInput)
    );
  },
  errorMessage(dateInput, field) {
    if (required && !validators.dateRequiredValidator(dateInput)) {
      return {
        required: translate(requiredErrorMessage, {
          field: plainTextFromDangerouslySetInnerHtml(field)
        })
      };
    }
    if (!validators.dateFormatValidator(dateInput)) {
      return {
        dateFormatInvalid: translate(
          'NucleusWidgets_Validations_DateFormat__resx',
          { format: getLocaleDateString(locale, formatOptions) }
        )
      };
    }
    if (!validators.timeFormatValidator(dateInput, useSeconds)) {
      return {
        timeFormatInvalid: translate(
          'NucleusWidgets_Validations_TimeRequired__resx',
          { field: plainTextFromDangerouslySetInnerHtml(field) }
        )
      };
    }
    if (!validators.dateRangeValidator(dateInput)) {
      if (minDate && maxDate) {
        return {
          dateRange: translate('NucleusWidgets_Validations_DateBetween__resx', {
            startDate: new Date(minDate),
            endDate: new Date(maxDate)
          })
        };
      } else if (maxDate) {
        return {
          dateRange: translate('NucleusWidgets_Validations_DateBefore__resx', {
            endDate: new Date(maxDate)
          })
        };
      } else if (minDate) {
        return {
          dateRange: translate('NucleusWidgets_Validations_DateAfter__resx', {
            startDate: new Date(minDate)
          })
        };
      }
    }
  }
});

export const creditCardNumber = (translate, cardName) => {
  return {
    validator: (value, cardType) => {
      return validators.creditCardValidator(cardType)(value);
    },
    errorMessage(value, field) {
      return translate(
        'NucleusWidgets_Validations_CreditCardFormatFormat__resx',
        { field: plainTextFromDangerouslySetInnerHtml(field), cardName }
      );
    }
  };
};
